/**
 * Oturum orta yerde düşerse ne olur — S37'nin bağlantı noktası.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TEK İŞ: "sunucu 401 dedi" olayını oturum durumuna çevirmek.
 *
 * `lib/api.ts` React'i tanımaz (tanısaydı her test bir sağlayıcı kurmak
 * zorunda kalırdı); `RequireSession` de tek tek isteklerin sonucunu görmez.
 * Aradaki tercümanı bu dosya yapar — bağlama tek satır:
 *
 *     oturumDusmesiniIzle(queryClient)   // main.tsx
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── NEDEN `qc.clear()` DEĞİL ────────────────────────────────────────────────
 * 401'de tüm önbelleği temizlemek ilk akla gelen çözümdür ve SONSUZ DÖNGÜ
 * üretir: `clear()` aktif sorguları düşürür, aktif sorgular hemen yeniden
 * getirilir, her biri yine 401 alır, her 401 yine `clear()` çağırır. Ekran
 * donar, sunucu saniyede onlarca istek yer.
 *
 * Burada YALNIZ oturum anahtarına yazılır ve HİÇBİR yeniden getirme
 * tetiklenmez. `setQueryData` bir istek başlatmaz; yalnız `RequireSession`'ı
 * yeniden çizer, o da yönlendirir ve ekranlar zaten sökülür.
 *
 * Eski kullanıcının verisi önbellekte kalır — o temizlik GİRİŞ anında yapılır
 * (`LoginPage`), çünkü orada sökülecek ekran ve tetiklenecek sorgu yoktur.
 */
import type { QueryClient } from '@tanstack/react-query';

import { oturumDustugunde } from '../lib/api.ts';
import { SESSION_KEY, type Session } from './session.ts';

/**
 * "Oturum ELDEYKEN düştü" bayrağı.
 *
 * Giriş ekranına iki yoldan gelinir ve ikisi aynı şey DEĞİLDİR:
 *   • hiç giriş yapılmamış  → normal giriş ekranı
 *   • oturum kullanılırken düştü → "ne oldu?" sorusunun cevabı gerekir
 *
 * Bayrak önbellekte tutuluyor, modül değişkeninde değil: `queryClient` ile
 * aynı ömrü paylaşır, çıkışta `qc.clear()` ile kendiliğinden silinir ve
 * testler birbirine sızdırmaz.
 */
export const OTURUM_SONA_ERDI_KEY = ['auth', 'sona-erdi'] as const;

/**
 * 401 bildirimini oturum durumuna bağlar. Dönüş değeri aboneliği bırakır.
 *
 * İki kez çağrılırsa iki dinleyici olur; uygulamada bir kez `main.tsx`'te
 * çağrılır, testler dönen fonksiyonla temizler.
 */
export function oturumDusmesiniIzle(qc: QueryClient): () => void {
  return oturumDustugunde(() => {
    const oturum = qc.getQueryData<Session | null>(SESSION_KEY);

    /*
     * Elde oturum yoksa kaybedilecek oturum da yoktur.
     *
     * Bu satır iki şeyi birden yapar:
     *   1. Paralel üç istek aynı anda 401 alırsa yalnız İLKİ iş yapar —
     *      sonrakiler `null` görüp döner (tek yönlendirme, tek bayrak).
     *   2. Hiç giriş yapmamış kullanıcının isteği 401 alırsa "oturumunuz
     *      sona erdi" YALANI söylenmez (`undefined` de buraya düşer).
     */
    if (!oturum) return;

    // Sıra önemli: bayrak ÖNCE. `SESSION_KEY` yazımı yeniden çizimi
    // tetikler, ve o çizimde `RequireSession` bayrağı okuyacak.
    qc.setQueryData(OTURUM_SONA_ERDI_KEY, true);
    qc.setQueryData(SESSION_KEY, null);
  });
}
